import FilterAltOffIcon from '@material-design-icons/svg/filled/filter_alt_off.svg?react';
import TuneIcon from '@material-design-icons/svg/filled/tune.svg?react';
import type { SortingState } from '@tanstack/react-table';
import { useState } from 'react';
import type {
  ColumnVisibilityState,
  HubspotColumnKey,
} from '../../../lib/hubspot-management/columnVisibility';
import {
  hubspotRequestTypeOptions,
  hubspotSortOptions,
  hubspotSourceOptions,
} from '../../../lib/hubspot-management/options';
import { cn } from '../../../lib/utils';
import {
  SortMenu,
  ToolbarSearch,
  ToolbarSelect,
} from '../../management/ListToolbar';
import { HubspotColumnVisibilityMenu } from '../filters/HubspotColumnVisibilityMenu';

export interface HubspotTicketFilters {
  pipeline: string;
  requestType: string;
  source: string;
  assignee: string;
  search: string;
}

export type HubspotTicketFilterKey = Exclude<
  keyof HubspotTicketFilters,
  'search'
>;

interface HubspotTicketsToolbarProps {
  filters: HubspotTicketFilters;
  viewerUserId: string;
  pipelineStageOptions: Array<{ value: string; label: string }>;
  assigneeOptions: Array<{ value: string; label: string }>;
  sorting: SortingState;
  columnVisibility: ColumnVisibilityState;
  /** Narrow layouts (phones, or the navigator beside an open ticket). */
  compact: boolean;
  showColumnMenu: boolean;
  onSearchChange: (value: string) => void;
  onFilterChange: (key: HubspotTicketFilterKey, value: string) => void;
  onSortingChange: (sorting: SortingState) => void;
  onColumnVisibilityChange: (key: HubspotColumnKey, checked: boolean) => void;
  onClearFilters: () => void;
}

const UNASSIGNED_FILTER_VALUE = '_unassigned';

const controlClassName =
  'inline-flex h-9 items-center gap-1.5 rounded-lg border border-neutral-800 bg-neutral-900 px-3 font-medium text-neutral-300 transition-colors hover:border-neutral-700 hover:bg-neutral-800 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-teal-500';

export function HubspotTicketsToolbar({
  filters,
  viewerUserId,
  pipelineStageOptions,
  assigneeOptions,
  sorting,
  columnVisibility,
  compact,
  showColumnMenu,
  onSearchChange,
  onFilterChange,
  onSortingChange,
  onColumnVisibilityChange,
  onClearFilters,
}: HubspotTicketsToolbarProps) {
  const segments = [
    { key: 'all', label: 'All', assignee: '' },
    { key: 'mine', label: 'Assigned to me', assignee: viewerUserId },
    {
      key: 'unassigned',
      label: 'Unassigned',
      assignee: UNASSIGNED_FILTER_VALUE,
    },
  ];
  const isSegmentAssignee = segments.some(
    (segment) => segment.assignee === filters.assignee,
  );
  const activeFilterCount =
    [filters.pipeline, filters.requestType, filters.source].filter(Boolean)
      .length + (isSegmentAssignee ? 0 : 1);
  const hasActiveFilters = Object.values(filters).some(Boolean);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(
    activeFilterCount > 0,
  );

  const filterSelects = (
    <>
      <ToolbarSelect
        inactiveValue=""
        label="Stage"
        value={filters.pipeline}
        options={pipelineStageOptions}
        onChange={(value) => onFilterChange('pipeline', value)}
      />
      <ToolbarSelect
        inactiveValue=""
        label="Request type"
        value={filters.requestType}
        options={hubspotRequestTypeOptions}
        onChange={(value) => onFilterChange('requestType', value)}
      />
      <ToolbarSelect
        inactiveValue=""
        label="Source"
        value={filters.source}
        options={hubspotSourceOptions}
        onChange={(value) => onFilterChange('source', value)}
      />
      <ToolbarSelect
        inactiveValue=""
        label="Assignee"
        value={filters.assignee}
        options={assigneeOptions}
        onChange={(value) => onFilterChange('assignee', value)}
      />
    </>
  );

  const clearButton = hasActiveFilters ? (
    <button
      type="button"
      onClick={onClearFilters}
      className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 font-medium text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white"
    >
      <FilterAltOffIcon className="size-4 fill-current" />
      Clear
    </button>
  ) : null;

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <div
          role="group"
          aria-label="Queue segment"
          className="flex max-w-full shrink-0 gap-0.5 overflow-x-auto rounded-lg bg-neutral-900 p-0.5 ring-1 ring-neutral-800 scrollbar-hide"
        >
          {segments.map((segment) => {
            const isActive = filters.assignee === segment.assignee;

            return (
              <button
                key={segment.key}
                type="button"
                aria-pressed={isActive}
                onClick={() => onFilterChange('assignee', segment.assignee)}
                className={cn(
                  'whitespace-nowrap rounded-md px-3 py-1.5 font-medium transition-colors',
                  isActive
                    ? 'bg-neutral-800 text-white shadow-sm'
                    : 'text-neutral-400 hover:text-neutral-200',
                )}
              >
                {segment.label}
              </button>
            );
          })}
        </div>

        <div className="min-w-[180px] flex-1 text-base sm:text-sm">
          <ToolbarSearch
            label="Search tickets"
            placeholder="Search name, package, ticket…"
            value={filters.search}
            onChange={onSearchChange}
          />
        </div>

        {compact ? (
          <button
            type="button"
            aria-expanded={isFilterPanelOpen}
            onClick={() => setIsFilterPanelOpen((open) => !open)}
            className={cn(
              controlClassName,
              isFilterPanelOpen &&
                'border-neutral-700 bg-neutral-800 text-white',
            )}
          >
            <TuneIcon className="size-4 fill-current text-neutral-400" />
            Filters
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-teal-500/20 px-1.5 text-[11px] leading-4 text-teal-200">
                {activeFilterCount}
              </span>
            )}
          </button>
        ) : (
          <>
            {filterSelects}
            <SortMenu
              label="Sort tickets"
              options={hubspotSortOptions}
              sorting={sorting}
              onSortingChange={onSortingChange}
            />
            {showColumnMenu && (
              <HubspotColumnVisibilityMenu
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={onColumnVisibilityChange}
                triggerClassName={controlClassName}
              />
            )}
            {clearButton}
          </>
        )}
      </div>

      {compact && isFilterPanelOpen && (
        <div className="grid grid-cols-2 gap-2 rounded-xl border border-neutral-800 bg-neutral-900/60 p-2 text-base sm:text-sm">
          {filterSelects}
          <SortMenu
            label="Sort tickets"
            options={hubspotSortOptions}
            sorting={sorting}
            onSortingChange={onSortingChange}
            className="col-span-2 justify-start"
          />
          {clearButton && <div className="col-span-2 flex">{clearButton}</div>}
        </div>
      )}
    </div>
  );
}
